import React, { useCallback, useEffect, useRef, useState } from "react";
import { ChevronRight } from "lucide-react";
import { STATUS_BEGIN, STATUS_FAIL } from "utils/constants/others";
import { SwipeLabel, SwipeThumb, SwipeTrack } from "./styles.publicMarkEntry";
import { Spinner } from "./styles.shippingNoteDetails";

const SWIPE_COMPLETE_RATIO = 0.82;
const THUMB_SIZE = 66;

const TripleChevron = () => (
  <>
    <ChevronRight aria-hidden="true" />
    <ChevronRight aria-hidden="true" />
    <ChevronRight aria-hidden="true" />
  </>
);

const SwipeToMarkEntry = ({ label, status, onConfirm, disabled }) => {
  const trackRef = useRef(null);
  const draggingRef = useRef(false);
  const [dragging, setDragging] = useState(false);
  const [dragX, setDragX] = useState(0);
  const busy = status === STATUS_BEGIN;

  const getMaxDrag = useCallback(() => {
    const trackWidth = trackRef.current?.offsetWidth || 0;
    return Math.max(trackWidth - THUMB_SIZE - 8, 0);
  }, []);

  const handlePointerDown = (event) => {
    if (disabled || busy) return;
    draggingRef.current = true;
    setDragging(true);
    event.currentTarget.setPointerCapture?.(event.pointerId);
  };

  const handlePointerMove = (event) => {
    if (!draggingRef.current || !trackRef.current) return;
    const trackRect = trackRef.current.getBoundingClientRect();
    const nextX = event.clientX - trackRect.left - THUMB_SIZE / 2;
    setDragX(Math.min(Math.max(nextX, 0), getMaxDrag()));
  };

  const finishDrag = () => {
    if (!draggingRef.current) return;
    draggingRef.current = false;
    setDragging(false);
    const maxDrag = getMaxDrag();
    const completed = maxDrag > 0 && dragX >= maxDrag * SWIPE_COMPLETE_RATIO;
    setDragX(completed ? maxDrag : 0);
    if (completed) onConfirm();
  };

  useEffect(() => {
    if (status === STATUS_FAIL) setDragX(0);
  }, [status]);

  return (
    <SwipeTrack ref={trackRef} $disabled={disabled}>
      <SwipeLabel $disabled={disabled}>{label}</SwipeLabel>
      <SwipeThumb
        style={{ transform: `translate(${dragX}px, -50%)` }}
        $dragging={dragging}
        $busy={busy}
        $disabled={disabled}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={finishDrag}
        onPointerCancel={finishDrag}
      >
        {busy ? <Spinner aria-label="Marking entry" /> : <TripleChevron />}
      </SwipeThumb>
    </SwipeTrack>
  );
};

export default SwipeToMarkEntry;
